import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone, Star, Clock, CalendarCheck } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, IMG } from "@/lib/site";

const WindStreaksRadial = () => (
  <div aria-hidden="true" className="pointer-events-none absolute -inset-[18%] z-[-1]">
    {[
      { left: "2%", height: 190, delay: 0, duration: 1.1, dir: -1 },
      { left: "16%", height: 240, delay: 0.3, duration: 0.9, dir: -1 },
      { left: "30%", height: 170, delay: 0.15, duration: 1.3, dir: -1 },
      { left: "43%", height: 220, delay: 0.5, duration: 1.0, dir: -1 },
      { left: "56%", height: 200, delay: 0.25, duration: 1.2, dir: 1 },
      { left: "69%", height: 250, delay: 0.6, duration: 0.95, dir: 1 },
      { left: "82%", height: 180, delay: 0.1, duration: 1.25, dir: 1 },
      { left: "93%", height: 230, delay: 0.45, duration: 1.05, dir: 1 },
    ].map((w, i) => (
      <motion.span
        key={i}
        className="absolute bottom-0 block w-[12px] rounded-full"
        style={{
          left: w.left,
          height: w.height,
          background:
            "linear-gradient(0deg, transparent, rgba(255,255,255,0.4), transparent)",
        }}
        initial={{ y: "20vh", x: 0, rotate: 0, opacity: 0 }}
        animate={{
          y: "-90vh",
          x: w.dir > 0 ? "14vw" : "-14vw",
          rotate: w.dir * 26,
          opacity: [0, 1, 1, 0],
        }}
        transition={{
          repeat: Infinity,
          duration: w.duration,
          delay: w.delay,
          ease: "linear",
        }}
      />
    ))}
  </div>
);

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 80, damping: 18, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const chips = [
  { icon: Star, text: "Price online before you book" },
  { icon: CalendarCheck, text: "Pick your day, time — even your tradie" },
  { icon: Clock, text: "90% cleared in the first hour" },
];

const WIND = [
  { left: "4%", height: 220, delay: 0, duration: 0.9 },
  { left: "16%", height: 160, delay: 0.3, duration: 1.2 },
  { left: "29%", height: 280, delay: 0.1, duration: 0.8 },
  { left: "43%", height: 200, delay: 0.45, duration: 1.1 },
  { left: "56%", height: 260, delay: 0.2, duration: 0.85 },
  { left: "68%", height: 170, delay: 0.55, duration: 1.3 },
  { left: "80%", height: 240, delay: 0.35, duration: 1.0 },
  { left: "91%", height: 190, delay: 0.65, duration: 1.15 },
];

const WindStreaks = () => (
  <div aria-hidden="true" className="pointer-events-none absolute -inset-[18%] z-[-1]">
    {WIND.map((w, i) => (
      <motion.span
        key={i}
        className="absolute bottom-0 block w-[12px] rounded-full"
        style={{
          left: w.left,
          height: w.height,
          background:
            "linear-gradient(0deg, transparent, rgba(255,255,255,0.4), transparent)",
        }}
        initial={{ y: "30vh", opacity: 0 }}
        animate={{ y: "-120vh", opacity: [0, 1, 1, 0] }}
        transition={{
          repeat: Infinity,
          duration: w.duration,
          delay: w.delay,
          ease: "linear",
        }}
      />
    ))}
  </div>
);

export default function Hero() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 800], [0, 160]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pb-20 pt-32 lg:min-h-[120vh] lg:justify-start lg:pb-6 lg:pt-[5.25rem]"
    >
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <img
          src={IMG.heroPipes}
          alt=""
          className="h-[120%] w-full object-cover opacity-25 grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/60 via-[#09090b]/80 to-[#09090b]" />
      </motion.div>

      <motion.div
        initial={{ y: "-130%", rotate: -10, opacity: 0 }}
        animate={{ y: 0, rotate: -4, opacity: 1 }}
        transition={{ delay: 1.2, type: "spring", stiffness: 32, damping: 13 }}
        className="pointer-events-none absolute bottom-0 right-0 z-10 hidden w-80 select-none sm:w-96 md:w-[36rem] lg:bottom-auto lg:right-[8%] lg:top-[38%] lg:block lg:w-[38vw]"
      >
        <WindStreaks />
        <motion.img
          src="/tradie.png"
          alt="Rolf, blockage expert at The Plumbing & Electrical Co."
          className="w-full"
          style={{
            filter: "drop-shadow(0 24px 40px rgba(0,0,0,0.6)) drop-shadow(0 0 30px rgba(173,255,1,0.15))",
          }}
          data-testid="hero-tradie"
          animate={{
            y: [0, -38, -12, -52, -22, 0],
            x: [0, 30, -22, 14, -8, 0],
            rotate: [0, 8, -7, 4, -3, 0],
          }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut", delay: 2.2 }}
        />
      </motion.div>

      <motion.img
        src={IMG.logo}
        alt="The Plumbing & Electrical Co. logo"
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 70, damping: 12 }}
        className="pointer-events-none absolute right-[8%] top-[7%] z-[5] hidden w-[36vw] select-none lg:block"
        style={{
          filter: "drop-shadow(0 0 34px rgba(22,158,217,0.55))",
        }}
        data-testid="hero-logo-badge"
      />

      <div className="w-full px-4 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6 flex items-center gap-4"
        >
          <img
            src={IMG.logo}
            alt="The Plumbing & Electrical Co. logo"
            className="h-14 w-14 rounded-full border-2 border-[#adff01] object-cover md:h-16 md:w-16"
            data-testid="hero-logo"
          />
          <p
            className="inline-block border-2 border-[#169ed9] bg-[#169ed9]/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.3em] text-[#169ed9] md:text-xs"
            data-testid="hero-eyebrow"
          >
            Blocked Drains Perth — Cleared Fast
          </p>
        </motion.div>

        <h1 className="font-display text-[17vw] uppercase leading-[0.85] tracking-tighter sm:text-[13vw] lg:text-[8vw]">
          <MaskedLine delay={0.15}>Blocked</MaskedLine>
          <MaskedLine delay={0.28} className="text-outline">
            Drains?
          </MaskedLine>
          <MaskedLine delay={0.41}>
            <span className="text-[#adff01]">Blasted.</span>
          </MaskedLine>
        </h1>

        <motion.div
          initial={{ y: "-100vh", opacity: 0, rotate: -10 }}
          animate={{ y: 0, opacity: 1, rotate: -4 }}
          transition={{ delay: 3, type: "spring", stiffness: 32, damping: 13 }}
          className="relative mx-auto my-10 w-64 select-none sm:w-80 lg:hidden"
        >
          <WindStreaksRadial />
          <motion.div
            animate={{
              scale: [1, 1, 1.1, 0.9, 1.04, 1, 1],
              rotate: [0, 0, -9, 5, -2, 0, 0],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              times: [0, 0.72, 0.78, 0.83, 0.88, 0.93, 1],
              ease: "easeInOut",
              delay: 5,
            }}
          >
            <motion.img
              src="/tradie.png"
              alt="Rolf, blockage expert at The Plumbing & Electrical Co."
              className="w-full"
              style={{
                filter: "drop-shadow(0 18px 30px rgba(0,0,0,0.6))",
              }}
              data-testid="hero-tradie-mobile"
              animate={{
                y: [0, -38, -12, -52, -22, 0],
                x: [0, 30, -22, 14, -8, 0],
                rotate: [0, 8, -7, 4, -3, 0],
              }}
              transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut", delay: 5 }}
            />
          </motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 40, scale: 0.8, rotate: -3 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
          transition={{ delay: 4, type: "spring", stiffness: 90, damping: 12 }}
          className="mt-8 font-display text-lg uppercase tracking-tight text-[#ff66c4] md:text-2xl"
          data-testid="hero-tagline"
        >
          Heroes don't wear capes. They carry plungers!
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-4 max-w-xl text-base font-medium leading-relaxed text-zinc-300 md:text-lg"
          data-testid="hero-subcopy"
        >
          When you search <span className="font-bold text-white">blocked drains Perth</span>,
          you want three things: the price upfront, availability to book, and the
          blockage fixed. We take on your blockage with a{" "}
          <span className="font-bold text-white">5000 PSI drain jetter</span> that
          cuts through tree roots and blasts your pipes clean. See the price online,
          booking online, choose your exact date and time — Australia's only plumbing
          crew that lets you do all three!
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <motion.a
            href="#book-it-in"
            data-testid="hero-book-button"
            whileHover={{ x: -3, y: -3 }}
            className="shadow-hard-green flex items-center gap-2 border-2 border-black bg-[#ff66c4] px-7 py-4 font-display text-sm uppercase tracking-wide text-black transition-shadow hover:shadow-[4px_4px_0_0_#adff01] md:text-base"
          >
            Clear My Blockage
            <ArrowUpRight className="h-5 w-5" strokeWidth={3} />
          </motion.a>
          <motion.a
            href={PHONE_TEL}
            data-testid="hero-call-button"
            whileHover={{ x: -3, y: -3 }}
            className="shadow-hard-white flex items-center gap-2 border-2 border-black bg-[#adff01] px-7 py-4 font-display text-sm uppercase tracking-wide text-black transition-shadow hover:shadow-[4px_4px_0_0_#fff] md:text-base"
          >
            <Phone className="h-5 w-5" strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </motion.a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95 }}
          className="mt-12 flex flex-col items-start gap-y-4"
        >
          {chips.map(({ icon: Icon, text }) => (
            <li
              key={text}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400 md:text-sm"
            >
              <Icon className="h-4 w-4 text-[#adff01]" strokeWidth={2.5} />
              {text}
            </li>
          ))}
        </motion.ul>

      </div>
    </section>
  );
}
